import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Fuel, Search, Store, Check, Receipt } from 'lucide-react';
import { Input } from '../ui/input';
import { SupplierPicker } from './CostFieldsPanel';
import { AccountNumberCard, accountFor } from '../portal/orders/SupplierAccount';
import { legacyJobCode } from '../../utils/jobCode';

/** LC1081 → LC-1081, LUX219 stays LUX219. */
export const displayJobCode = (ref) => {
  const code = legacyJobCode(ref) || '';
  return /^LC\d/i.test(code) ? code.replace(/^LC/i, 'LC-') : code;
};
export const projectCompany = (p) => (p?.project_type === 'CON' || /^LC/i.test(p?.project_ref || '') ? 'CON' : 'DEV');
const isPinnedFor = (s, company) => (company === 'CON' ? !!s.pinned_lc : !!s.pinned_ld);

/** Capture step 1 — company, supplier (account number), then the job. */
export function SupplierStep({ api, suppliers, company, onCompany, supplier, project, lockedProject, onSupplier, onProject, onXero }) {
  const [otherOpen, setOtherOpen] = useState(false);
  const [projects, setProjects] = useState(null);
  const [q, setQ] = useState('');

  const pinned = useMemo(() => suppliers.filter(s => isPinnedFor(s, company)), [suppliers, company]);
  const rest = useMemo(() => suppliers.filter(s => !isPinnedFor(s, company)), [suppliers, company]);
  const isCurated = supplier && suppliers.some(s => s.id === supplier.order_supplier_id);
  const isOther = supplier && !supplier.none && !isCurated;

  useEffect(() => {
    if (lockedProject) return;
    api.get('/projects', { params: { status: 'Live', limit: 200 } }).then(r => setProjects(r.data || [])).catch(() => setProjects([]));
  }, [api, lockedProject]);

  const pickCurated = (s) => { onSupplier({ order_supplier_id: s.id, supplier_id: s.contact_id || null, name: s.name, curated: s }); setOtherOpen(false); };
  const shown = useMemo(() => {
    const list = (projects || []).filter(p => projectCompany(p) === company);
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter(p => [p.project_ref, p.client_name, p.address_line1, displayJobCode(p.project_ref)].some(v => (v || '').toLowerCase().includes(needle)));
  }, [projects, q, company]);

  const bigBtn = 'w-full flex items-center gap-2.5 rounded-xl border px-3 h-12 text-left text-sm font-semibold transition-all active:scale-[0.98]';

  return (
    <div className="space-y-5" data-testid="capture-supplier-step">
      {lockedProject ? (
        <div className="rounded-xl border border-blue-500/30 bg-blue-500/10 px-3 py-2.5 flex items-center gap-3" data-testid="capture-job-locked">
          <span className="font-mono font-bold text-[#F5A623]">{displayJobCode(lockedProject.project_ref)}</span>
          <span className="text-sm text-gray-200 truncate">{lockedProject.client_name || lockedProject.address_line1 || ''}</span>
          <span className="ml-auto text-[10px] text-blue-300 whitespace-nowrap">From diary</span>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-1 rounded-xl bg-[#1E2530] border border-white/10 p-1" data-testid="capture-company-toggle">
          {[['DEV', 'LUX Developments'], ['CON', 'LUX Construction']].map(([key, label]) => (
            <button key={key} type="button" onClick={() => onCompany(key)} data-testid={`capture-company-${key}`}
              className={`h-9 rounded-lg text-xs font-semibold transition-colors ${company === key ? (key === 'CON' ? 'bg-blue-500/20 text-blue-200 border border-blue-500/40' : 'bg-[#F5A623]/15 text-[#F5A623] border border-[#F5A623]/40') : 'text-gray-400 hover:text-gray-200'}`}>
              {label}
            </button>
          ))}
        </div>
      )}

      <section>
        <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2">{lockedProject ? 'Supplier' : '1 · Supplier'}</div>
        {pinned.length > 0 && (
          <div className={`grid gap-2 ${pinned.length === 1 ? 'grid-cols-1' : pinned.length === 2 || pinned.length === 4 ? 'grid-cols-2' : 'grid-cols-3'}`}>
            {pinned.map(s => {
              const on = supplier?.order_supplier_id === s.id;
              const acc = accountFor(s, company);
              return (
                <button key={s.id} type="button" onClick={() => pickCurated(s)} data-testid={`capture-supplier-pin-${s.id}`}
                  className={`rounded-xl border px-2 py-3 text-center transition-all active:scale-[0.97] ${on ? 'border-[#F5A623] bg-[#F5A623]/10' : 'border-white/10 bg-[#2A3240] hover:border-[#F5A623]/40'}`}>
                  <div className="text-sm font-semibold text-white leading-tight">{s.name}</div>
                  <div className={`block text-[10px] font-mono mt-1 ${acc ? 'text-[#F5A623]' : 'text-gray-500'}`}>{acc || 'no account'}</div>
                </button>
              );
            })}
          </div>
        )}
        <div className="relative mt-2">
          <button type="button" onClick={() => setOtherOpen(o => !o)} data-testid="capture-supplier-other"
            className={`${bigBtn} ${isOther || (isCurated && !pinned.some(p => p.id === supplier.order_supplier_id)) ? 'border-[#F5A623] bg-[#F5A623]/10 text-white' : 'border-white/10 bg-[#2A3240] text-gray-200'}`}>
            <Store className="w-4 h-4 text-gray-400" />
            <span className="flex-1">{isOther || (isCurated && !pinned.some(p => p.id === supplier.order_supplier_id)) ? supplier.name : 'Other supplier…'}</span>
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${otherOpen ? 'rotate-180' : ''}`} />
          </button>
          {otherOpen && (
            <div className="mt-2 rounded-xl border border-white/10 bg-[#1E2530] p-3 space-y-3" data-testid="capture-supplier-other-menu">
              {rest.length > 0 && (
                <div className="grid grid-cols-2 gap-1.5">
                  {rest.map(s => (
                    <button key={s.id} type="button" onClick={() => pickCurated(s)} data-testid={`capture-supplier-curated-${s.id}`}
                      className={`flex items-center justify-between rounded-lg border px-3 py-2 text-left text-sm ${supplier?.order_supplier_id === s.id ? 'border-[#F5A623] text-white' : 'border-white/10 text-gray-200 hover:border-white/30'}`}>
                      <span>{s.name}{accountFor(s, company) && <span className="ml-1.5 font-mono text-[10px] text-[#F5A623]">{accountFor(s, company)}</span>}</span>
                      {supplier?.order_supplier_id === s.id && <Check className="w-4 h-4 text-[#F5A623]" />}
                    </button>
                  ))}
                </div>
              )}
              <SupplierPicker supplierId={isOther ? supplier.supplier_id : null} supplierName={isOther ? supplier.name : null}
                onPick={(sup) => { if (sup) { onSupplier({ order_supplier_id: null, supplier_id: sup.id, name: sup.name, curated: null }); setOtherOpen(false); } }} />
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <button type="button" onClick={() => { onSupplier({ none: true, order_supplier_id: null, supplier_id: null, name: '', curated: null }); setOtherOpen(false); }} data-testid="capture-supplier-none"
            className={`${bigBtn} ${supplier?.none ? 'border-[#F5A623] bg-[#F5A623]/10 text-white' : 'border-white/10 bg-[#2A3240] text-gray-200 hover:border-white/30'}`}>
            <Receipt className="w-4 h-4 text-gray-400" /> No supplier
          </button>
          <button type="button" onClick={onXero} data-testid="capture-xero-row"
            className={`${bigBtn} border-white/10 bg-[#2A3240] text-gray-200 hover:border-white/30`}>
            <Fuel className="w-4 h-4 text-gray-400" /> <span className="truncate">Xero — fuel, tools etc</span>
          </button>
        </div>
      </section>

      {supplier?.curated && accountFor(supplier.curated, company) && (
        <AccountNumberCard supplier={supplier.curated} company={company} />
      )}

      {supplier && !lockedProject && (
        <section data-testid="capture-job-step">
          <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2">2 · Which job? <span className="normal-case tracking-normal font-normal text-gray-600">· {company === 'CON' ? 'LUX Construction' : 'LUX Developments'}</span></div>
          <div className="relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search job, client or address…" className="pl-9 bg-[#2A3240] border-white/20 text-white" data-testid="capture-job-search" />
          </div>
          <div className="max-h-[38vh] overflow-y-auto space-y-1.5 mt-2">
            {projects === null && <p className="text-xs text-gray-500 text-center py-4">Loading jobs…</p>}
            {shown.map(p => {
              const on = project?.id === p.id;
              return (
                <button key={p.id} type="button" onClick={() => onProject(p)} data-testid={`capture-job-${p.id}`}
                  className={`w-full flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-all active:scale-[0.98] ${on ? 'border-[#F5A623] bg-[#F5A623]/10' : 'border-white/5 bg-[#2A3240] hover:border-[#F5A623]/30'}`}>
                  <span className="font-mono font-bold text-[#F5A623] text-sm min-w-[72px]">{displayJobCode(p.project_ref)}</span>
                  <span className="text-sm text-gray-200 truncate flex-1">{p.client_name || p.address_line1 || ''}</span>
                </button>
              );
            })}
            {projects && shown.length === 0 && <p className="text-xs text-gray-500 text-center py-4">No live {company === 'CON' ? 'LUX Construction' : 'LUX Developments'} jobs match — try the other company above</p>}
          </div>
        </section>
      )}

    </div>
  );
}
